import React, { ChangeEvent, useState } from 'react'
import {
  FormCheckboxField,
  FormErrorText,
  FormField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ClientPhoneNumberDto } from 'api/Clients/Types/ClientDetailedDto'
import { CountryDto } from 'api/Identity/Types/Country'
import { useCountriesToCountryCodes } from 'hooks/useCountriesToCountryCodes'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  countries: CountryDto[]
  phoneNumbers: ClientPhoneNumberDto[]
  data: ClientPhoneNumberDto
  clientId: string
  handleChange(values: ClientPhoneNumberDto): void
}

const initialPhoneState = (data: ClientPhoneNumberDto) => ({
  id: data.id,
  tempId: data.tempId,
  type: 'Personal',
  number: data.number,
  countryCode: data.countryCode,
  isPrimary: data.isPrimary,
  isVerified: data.isVerified,
  isUsedIn2FA: data.isUsedIn2FA,
})

export const PhoneModal: React.FC<OuterProps> = ({
  isOpen,
  onClose,
  countries = [],
  phoneNumbers,
  handleChange,
  data,
  clientId,
}) => {
  const [form, setForm] = useState<ClientPhoneNumberDto>(initialPhoneState(data))
  const [error, setErrors] = useState<string>('')
  const uniqueCountryCodes = useCountriesToCountryCodes(countries)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const handleValidateForm = async () => {
    try {
      const phoneNumber = form.countryCode + form.number

      await clientsApiClient.validateClientPhoneNumber(clientId, phoneNumber)

      if (!form.number) {
        setErrors('Required')
        return
      }

      const isPrimaryExists = phoneNumbers.find((x) => x.id !== form.id && x.isPrimary)
      if (!form.isPrimary && !isPrimaryExists) {
        setErrors(
          'You need select at least one primary Phone Number. If primary number exists, please save him first.'
        )
        return
      }

      const isDuplicate = phoneNumbers.some(
        (x) => x.countryCode === form.countryCode && x.number === form.number && x.id !== form.id
      )

      if (isDuplicate) {
        setErrors('Phone number already exists.')
        return
      }

      handleChange(form)
    } catch (err) {
      console.error(err)
      setErrors('Please check your phone number')
    }
  }

  const handleSelectPhoneCode = (value: string | number | undefined) => {
    if (typeof value === 'string') {
      const country = countries.find((x) => x?.countryCodeNumber?.includes(value))
      setErrors('')
      setForm({ ...form, countryCode: country ? value : '' })
      return
    }

    setErrors('')
    setForm({ ...form, countryCode: '' })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{data?.id ? 'Edit Phone' : 'Add New Phone'}</DialogTitle>
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          <FormSelectField
            label='Country Code'
            value={form.countryCode}
            options={uniqueCountryCodes.map((countryCode) => ({
              id: countryCode,
              name: countryCode,
            }))}
            showSearch
            allowClear
            onChange={(value) => handleSelectPhoneCode(value)}
          />
          <FormField
            label='Number *'
            type='number'
            value={form.number}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setErrors('')
              setForm({ ...form, number: event.target.value })
            }}
          />
          <FormCheckboxField
            label='Primary'
            value={form.isPrimary}
            onChange={(checked) => setForm({ ...form, isPrimary: checked })}
          />
          <FormErrorText message={error} />
        </div>
        <DialogFooter>
          <Button onClick={onClose} type='button' variant='outline'>
            Cancel
          </Button>
          <Button disabled={!form.number} type='button' onClick={handleValidateForm}>
            {data.id ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
